// =============================================================================
// Lab table — dynamic row rendering.
//
// Builds one table row per marker the user entered in step 2. Each row shows:
//   - Marker name + severity dot (urgent/watch/normal) per zone
//   - Value (in SI units as stored)
//   - Unit string
//   - Reference range (per life stage for ferritin/Hb; fixed for others)
//   - Threshold statement (which stage / cutoff applies)
//   - Short zone-derived interpretation (pattern-agnostic)
//   - Evidence badge (from spec metadata)
//
// Pattern-specific narrative per marker (e.g., "consistent with stage 3
// erythropoiesis") is NOT rendered here — that belongs to the richer
// narrative section landing in a follow-up commit. This table stays value-
// and zone-driven.
// =============================================================================

import {
  anemiaThresholdForLifeStage,
  classifyCRP,
  classifyFerritin,
  classifyHemoglobin,
  classifyMCV,
  classifyRDW,
  classifyTSAT,
} from '../engine/thresholds.js';
import type {
  CRPZone,
  FerritinZone,
  HemoglobinZone,
  MCVZone,
  RDWZone,
  TSATZone,
} from '../engine/thresholds.js';
import type { EvidenceBadge, LabInputs, LifeStage } from '../engine/types.js';

/** Severity dot on the row — reflects this one marker's zone, not the overall pattern. */
export type DotSeverity = 'urgent' | 'watch' | 'normal';

export interface LabTableRow {
  markerLabel: string;
  value: string;
  unit: string;
  referenceRange: string;
  threshold: string;
  interpretation: string;
  dot: DotSeverity;
  badge: EvidenceBadge;
  badgeTip: string;
}

// -----------------------------------------------------------------------------
// Helpers — formatting + badge label
// -----------------------------------------------------------------------------

/** Format a numeric value for display. Strip trailing zeros, keep 1 decimal max. */
function fmtNum(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 10) / 10);
}

function badgeClass(badge: EvidenceBadge): string {
  switch (badge) {
    case 'HIGH_QUALITY':
      return 'high-quality';
    case 'CONSENSUS':
      return 'consensus';
    case 'PRELIMINARY':
      return 'preliminary';
  }
}

function badgeLabel(badge: EvidenceBadge): string {
  switch (badge) {
    case 'HIGH_QUALITY':
      return 'High quality';
    case 'CONSENSUS':
      return 'Consensus';
    case 'PRELIMINARY':
      return 'Preliminary';
  }
}

// -----------------------------------------------------------------------------
// Per-marker row builders
// -----------------------------------------------------------------------------

function ferritinRow(value: number, lifeStage: LifeStage): LabTableRow {
  const zone = classifyFerritin(value, lifeStage);
  const dot: DotSeverity = ferritinDot(zone);
  return {
    markerLabel: 'Ferritin',
    value: fmtNum(value),
    unit: 'μg/L',
    referenceRange: '15–150',
    threshold: ferritinThresholdLabel(zone, lifeStage),
    interpretation: ferritinInterpretation(zone),
    dot,
    badge: 'HIGH_QUALITY',
    badgeTip: 'Mei 2025, IPD analysis, 12 countries',
  };
}

function ferritinDot(zone: FerritinZone): DotSeverity {
  switch (zone) {
    case 'stage_3_ide':
    case 'stage_4_ida_range':
    case 'very_high':
    case 'overload_trigger':
      return 'urgent';
    case 'stage_1_incipient':
    case 'stage_2_depletion':
      return 'watch';
    case 'stage_0_replete':
    case 'elevated_normal':
      return 'normal';
  }
}

function ferritinThresholdLabel(zone: FerritinZone, lifeStage: LifeStage): string {
  switch (zone) {
    case 'stage_0_replete':
      return '≥ 50 (Stage 0)';
    case 'stage_1_incipient':
      return '30–50 (Stage 1)';
    case 'stage_2_depletion':
      return '25–30 (Stage 2)';
    case 'stage_3_ide':
      return stage3FerritinThreshold(lifeStage);
    case 'stage_4_ida_range':
      return '< 15 (Stage 4 range)';
    case 'elevated_normal':
      return '50–200 (upper normal)';
    case 'overload_trigger':
      return '> 200 with TSAT > 45 (overload)';
    case 'very_high':
      return '> 1000 μg/L';
  }
}

/** Stage-3 boundary varies by life stage — pregnancy + postpartum have distinct thresholds. */
function stage3FerritinThreshold(lifeStage: LifeStage): string {
  switch (lifeStage) {
    case 'pregnant_t1':
      return '< 25.8 (Stage 3, T1)';
    case 'pregnant_t2':
      return '< 18.3 (Stage 3, T2)';
    case 'pregnant_t3':
      return '< 19.0 (Stage 3, T3)';
    case 'postpartum_within_6mo':
      return '< 30 (Stage 3, postpartum)';
    default:
      return '< 25 (Stage 3)';
  }
}

function ferritinInterpretation(zone: FerritinZone): string {
  // Plain-language zone interpretations. 8th-grade reading level; avoids
  // "replete" / "repletion" / "erythropoiesis" per FF voice rules.
  switch (zone) {
    case 'stage_0_replete':
      return 'Iron stores look healthy. Enough on hand to make red blood cells.';
    case 'stage_1_incipient':
      return 'Slightly below the healthy range. Iron stores starting to drop.';
    case 'stage_2_depletion':
      return 'Iron reserves running low.';
    case 'stage_3_ide':
      return 'Low enough that the research documents iron running short for making red blood cells.';
    case 'stage_4_ida_range':
      return 'Very low. Fits the pattern for iron-deficiency anemia when hemoglobin is also low.';
    case 'elevated_normal':
      return 'Above the healthy range, below the overload threshold. Normal to elevated.';
    case 'overload_trigger':
      return 'Above the level that signals a check for iron overload when TSAT is also high.';
    case 'very_high':
      return 'Above 1000 μg/L. Talk with your health provider — this warrants clinical evaluation.';
  }
}

function hemoglobinRow(value: number, lifeStage: LifeStage): LabTableRow {
  const zone = classifyHemoglobin(value, lifeStage);
  const dot: DotSeverity =
    zone === 'severe_anemia' || zone === 'transfusion_territory' || zone === 'severe_postpartum'
      ? 'urgent'
      : zone === 'mild_anemia' || zone === 'moderate_anemia'
      ? 'watch'
      : 'normal';
  const anemia = anemiaThresholdForLifeStage(lifeStage);
  return {
    markerLabel: 'Hemoglobin',
    value: fmtNum(value),
    unit: 'g/L',
    referenceRange: `${anemia}–155`,
    threshold: `< ${anemia} (anemia)`,
    interpretation: hemoglobinInterpretation(zone),
    dot,
    badge: 'CONSENSUS',
    badgeTip: 'WHO 2024 commissioned individual-participant meta-analysis',
  };
}

function hemoglobinInterpretation(zone: HemoglobinZone): string {
  switch (zone) {
    case 'within_reference':
      return 'Within reference range. Not anemic.';
    case 'mild_anemia':
      return 'Below reference range. Mild anemia.';
    case 'moderate_anemia':
      return 'WHO moderate-anemia band (70–99 g/L).';
    case 'severe_anemia':
      return 'Severe anemia. Clinical evaluation recommended promptly.';
    case 'severe_postpartum':
      return 'Below 80 g/L within 6 months postpartum. Urgent tier.';
    case 'transfusion_territory':
      return 'Below 50 g/L. Transfusion territory — seek care today.';
  }
}

function tsatRow(value: number, lifeStage: LifeStage): LabTableRow {
  const zone = classifyTSAT(value, lifeStage);
  const dot: DotSeverity =
    zone === 'low' || zone === 'overload_trigger' ? 'urgent' : 'normal';
  return {
    markerLabel: 'TSAT',
    value: fmtNum(value),
    unit: '%',
    referenceRange: '20–50',
    threshold: '< 20 low',
    interpretation: tsatInterpretation(zone),
    dot,
    badge: 'HIGH_QUALITY',
    badgeTip: 'EASL 2022; Cacoub 2019',
  };
}

function tsatInterpretation(zone: TSATZone): string {
  switch (zone) {
    case 'low':
      return 'Below 20%. Reduced iron delivery to tissues.';
    case 'normal':
      return 'Within normal range.';
    case 'overload_trigger':
      return 'Elevated. Evaluate for iron overload.';
  }
}

function mcvRow(value: number): LabTableRow {
  const zone = classifyMCV(value);
  const dot: DotSeverity =
    zone === 'microcytic' ? 'urgent' : zone === 'macrocytic' ? 'watch' : 'normal';
  return {
    markerLabel: 'MCV',
    value: fmtNum(value),
    unit: 'fL',
    referenceRange: '80–100',
    threshold: '< 80 microcytic · > 100 macrocytic',
    interpretation: mcvInterpretation(zone),
    dot,
    badge: 'HIGH_QUALITY',
    badgeTip: 'WHO hematology reference ranges',
  };
}

function mcvInterpretation(zone: MCVZone): string {
  switch (zone) {
    case 'microcytic':
      return 'Below 80 fL. Small red cells — morphologic iron-shortage signal.';
    case 'normocytic':
      return 'Within normal range.';
    case 'macrocytic':
      return 'Above 100 fL. Larger than normal — B12/folate differential.';
  }
}

function rdwRow(value: number): LabTableRow {
  const zone = classifyRDW(value);
  const dot: DotSeverity = zone === 'elevated' ? 'watch' : 'normal';
  return {
    markerLabel: 'RDW',
    value: fmtNum(value),
    unit: '%',
    referenceRange: '11.5–14.5',
    threshold: '≥ 14.5 elevated',
    interpretation:
      zone === 'elevated'
        ? 'Elevated. Red cell size variability — often tracks with iron shortage.'
        : 'Within normal range.',
    dot,
    badge: 'PRELIMINARY',
    badgeTip: 'Multiple cohort studies',
  };
}

function crpRow(value: number): LabTableRow {
  const zone = classifyCRP(value);
  const dot: DotSeverity =
    zone === 'high_inflammation' ? 'urgent' : zone === 'mild_inflammation' ? 'watch' : 'normal';
  return {
    markerLabel: 'CRP',
    value: fmtNum(value),
    unit: 'mg/L',
    referenceRange: '< 5',
    threshold: '≥ 5 flags inflammation',
    interpretation: crpInterpretation(zone),
    dot,
    badge: 'CONSENSUS',
    badgeTip: 'BRINDA + Thurnham consensus guidance',
  };
}

function crpInterpretation(zone: CRPZone): string {
  switch (zone) {
    case 'no_inflammation':
      return 'Below inflammation threshold. Ferritin may be read at face value.';
    case 'mild_inflammation':
      return 'Above 5 mg/L. Ferritin may be inflated by inflammation.';
    case 'high_inflammation':
      return 'Above 10 mg/L. Ferritin interpretation unreliable.';
  }
}

function plainRow(
  label: string,
  value: number,
  unit: string,
  referenceRange: string,
  badge: EvidenceBadge,
  badgeTip: string,
): LabTableRow {
  return {
    markerLabel: label,
    value: fmtNum(value),
    unit,
    referenceRange,
    threshold: '—',
    interpretation: 'Supporting marker. Interpreted together with the primary iron panel.',
    dot: 'normal',
    badge,
    badgeTip,
  };
}

// -----------------------------------------------------------------------------
// Public API — build rows from inputs
// -----------------------------------------------------------------------------

export function buildRows(inputs: LabInputs): LabTableRow[] {
  const rows: LabTableRow[] = [];
  if (inputs.ferritin !== undefined) rows.push(ferritinRow(inputs.ferritin, inputs.lifeStage));
  if (inputs.hemoglobin !== undefined)
    rows.push(hemoglobinRow(inputs.hemoglobin, inputs.lifeStage));
  if (inputs.mcv !== undefined) rows.push(mcvRow(inputs.mcv));
  if (inputs.mch !== undefined)
    rows.push(plainRow('MCH', inputs.mch, 'pg', '27–33', 'HIGH_QUALITY', 'WHO reference ranges'));
  if (inputs.rdw !== undefined) rows.push(rdwRow(inputs.rdw));
  if (inputs.tsat !== undefined) rows.push(tsatRow(inputs.tsat, inputs.lifeStage));
  if (inputs.serumIron !== undefined)
    rows.push(
      plainRow(
        'Serum iron',
        inputs.serumIron,
        'μmol/L',
        '10.7–26.9',
        'CONSENSUS',
        'Day-to-day variability — interpret alongside TSAT',
      ),
    );
  if (inputs.tibc !== undefined)
    rows.push(
      plainRow('TIBC', inputs.tibc, 'μmol/L', '45–81', 'CONSENSUS', 'Often elevated in iron deficiency'),
    );
  if (inputs.stfr !== undefined)
    rows.push(
      plainRow(
        'sTfR',
        inputs.stfr,
        'mg/L',
        '2.2–5.0',
        'HIGH_QUALITY',
        'Tissue-level iron demand marker',
      ),
    );
  if (inputs.crp !== undefined) rows.push(crpRow(inputs.crp));
  if (inputs.retHe !== undefined)
    rows.push(
      plainRow(
        'Ret-He',
        inputs.retHe,
        'pg',
        '28–35',
        'PRELIMINARY',
        'Early iron-shortage signal in newest red cells',
      ),
    );
  return rows;
}

// -----------------------------------------------------------------------------
// DOM rendering
// -----------------------------------------------------------------------------

function rowHTML(row: LabTableRow): string {
  const valueStrong =
    row.dot === 'urgent' || row.dot === 'watch'
      ? `<strong>${row.value}</strong>`
      : row.value;
  return `
    <tr>
      <td>
        <span class="marker"><span class="dot ${row.dot}" data-tip="${dotTip(row.dot)}" aria-label="${titleCase(row.dot)}"></span>${row.markerLabel}</span>
      </td>
      <td class="num">${valueStrong}</td>
      <td class="num">${row.unit}</td>
      <td class="num">${row.referenceRange}</td>
      <td class="num">${row.threshold}</td>
      <td class="cell-interp">${row.interpretation}</td>
      <td><span class="badge ${badgeClass(row.badge)}" data-tip="${escapeAttr(row.badgeTip)}">${badgeLabel(row.badge)}</span></td>
    </tr>
  `;
}

function dotTip(dot: DotSeverity): string {
  switch (dot) {
    case 'urgent':
      return 'Urgent — below or above threshold';
    case 'watch':
      return 'Watch — at or near threshold';
    case 'normal':
      return 'Normal — within reference range';
  }
}

function titleCase(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;');
}

/**
 * Replace the table tbody with rows built from `inputs`. Idempotent — safe
 * to call repeatedly. If the table isn't on the page, does nothing.
 *
 * Caption is set from `lifeStageLabel` alone — the pattern name lives in the
 * page title, not the caption, so we don't repeat it as a code id here.
 */
export function renderLabTable(inputs: LabInputs, lifeStageLabel: string): void {
  const table = document.querySelector<HTMLTableElement>('table.lab-table');
  if (!table) return;

  const tbody = table.tBodies[0] ?? table.createTBody();
  const rows = buildRows(inputs);
  tbody.innerHTML = rows.map(rowHTML).join('');

  const caption = table.querySelector('caption');
  if (caption) {
    caption.textContent = `Unit system: SI · Reference range: ${lifeStageLabel}`;
  }
}
